import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Wand2, Loader2 } from 'lucide-react';

interface TaskGenerationContextModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (context: UserContext) => void;
  goalTitle: string;
  isLoading?: boolean;
}

export interface UserContext {
  progressSoFar: string;
  biggestChallenge: string;
  nextSteps: string;
}

const TaskGenerationContextModal: React.FC<TaskGenerationContextModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  goalTitle,
  isLoading = false
}) => {
  const [progressSoFar, setProgressSoFar] = useState('');
  const [biggestChallenge, setBiggestChallenge] = useState('');
  const [nextSteps, setNextSteps] = useState('');

  // Standard questions - always the same 3
  const questions = [
    "What would you like to accomplish?",
    "What's your biggest challenge right now?",
    "What do you think the next steps should be?"
  ];

  // Standard placeholders
  const placeholders = [
    "e.g., Set up my website",
    "e.g., Not sure how to get customers",
    "e.g., Launch marketing campaign"
  ];

  const handleSubmit = () => {
    // Always validate all 3 fields
    if (!progressSoFar.trim() || !biggestChallenge.trim() || !nextSteps.trim()) {
      return;
    }

    onSubmit({
      progressSoFar: progressSoFar.trim(),
      biggestChallenge: biggestChallenge.trim(),
      nextSteps: nextSteps.trim()
    });

    // Reset form and close modal
    setProgressSoFar('');
    setBiggestChallenge('');
    setNextSteps('');
    onClose();
  };

  const handleClose = () => {
    if (!isLoading) {
      setProgressSoFar('');
      setBiggestChallenge('');
      setNextSteps('');
      onClose();
    }
  };

  // Simple form validation - all 3 fields required
  const isFormValid = progressSoFar.trim() && biggestChallenge.trim() && nextSteps.trim();

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-amber-500" />
            Generate Tasks
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Render the 3 standard questions */}
          {questions.map((question, index) => {
            const values = [progressSoFar, biggestChallenge, nextSteps];
            const setters = [setProgressSoFar, setBiggestChallenge, setNextSteps];
            
            return (
              <div key={index} className="space-y-2">
                <Label htmlFor={`question-${index}`} className="text-sm font-medium">
                  {question}
                </Label>
                <Textarea
                  id={`question-${index}`}
                  placeholder={placeholders[index]}
                  value={values[index]}
                  onChange={(e) => setters[index](e.target.value)}
                  className="min-h-[60px] resize-none"
                  disabled={isLoading}
                />
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!isFormValid || isLoading}
            className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating Tasks...
              </>
            ) : (
              <>
                <Wand2 className="h-4 w-4 mr-2" />
                Generate Tasks
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TaskGenerationContextModal; 